import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveApplyApi } from '../api/leaveApplyApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

export function useLeaveApplyList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('All');
  const [datePresetFilter, setDatePresetFilter] = useState('Current Month');
  const [fromDate, setFromDate] = useState('2026-08-01');
  const [toDate, setToDate] = useState('2026-08-31');
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('Leave Type');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);


  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'All', label: 'All' },
  ];

  const leaveTypeOptions = [
    { value: 'All', label: 'All Leave Types' },
    { value: 'Casual Leave', label: 'Casual Leave' },
    { value: 'Sick Leave', label: 'Sick Leave' },
    { value: 'Privilege Leave', label: 'Privilege Leave' },
    { value: 'Maternity Leave', label: 'Maternity Leave' },
    { value: 'Compensatory Off', label: 'Compensatory Off' },
  ];

  const datePresetOptions = [
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Quarter', label: 'This Quarter' },
    { value: 'Custom Range', label: 'Custom Range' },
  ];

  const searchFieldOptions = [
    { value: 'Leave Type', label: 'Leave Type' },
    { value: 'Reason', label: 'Reason' },
    { value: 'Remarks', label: 'Remarks' },
  ];

  const tools = [
    { key: 'excel', label: 'Export to Excel', icon: 'table_view' },
    { key: 'pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
    { key: 'print', label: 'Print', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await leaveApplyApi.getList();
      setRecords(res.data || []);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Preset Change
  const handleDatePresetChange = useCallback((preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Month') {
      setFromDate('2026-08-01');
      setToDate('2026-08-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    }
  }, []);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Leave Type filter
      if (leaveTypeFilter !== 'All' && item.leaveType !== leaveTypeFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Leave Type' && !item.leaveType.toLowerCase().includes(query)) return false;
        if (searchField === 'Reason' && !item.reason.toLowerCase().includes(query)) return false;
        if (searchField === 'Remarks' && !item.remarks.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, statusFilter, leaveTypeFilter, searchText, searchField]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleView = useCallback(
    (record) => {
      navigate(`/leave-apply/entry?id=${record.id}&mode=view`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (record) => {
      navigate(`/leave-apply/entry?id=${record.id}&mode=edit`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = await appConfirm({ title: 'Delete Leave Application', message: 'Are you sure you want to delete this leave application?', confirmText: 'Delete', variant: 'danger' });
      if (confirmed) {
        await leaveApplyApi.delete(id);
        appToast.success('Leave application deleted successfully');
        loadData();
      }
    },
    [loadData]
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Filters applied');
  }, []);

  const resetFilters = useCallback(() => {
    setStatusFilter('All');
    setLeaveTypeFilter('All');
    setDatePresetFilter('Current Month');
    setFromDate('2026-08-01');
    setToDate('2026-08-31');
    setSearchText('');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('excel')) {
      appToast.success('Exporting Leave applications to Excel...');
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Leave applications to PDF...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      statusFilter,
      statusOptions,
      leaveTypeFilter,
      leaveTypeOptions,
      datePresetFilter,
      fromDate,
      toDate,
      searchText,
      searchField,
      isLoading,
      datePresetOptions,
      searchFieldOptions,
      tools,
    },
    actions: {
      setStatusFilter,
      setLeaveTypeFilter,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setSearchText,
      setSearchField,
      setPage,
      setPageSize,
      handleView,
      handleEdit,
      handleDelete,
      handleSearch,
      resetFilters,
      handleToolSelect,
      refresh: loadData,
    },
  };
}
